/* =========================================================================
   Match-3 pure helpers — DOM-free, self-tested. Shared by Faith Match
   (Gifts from Heaven) and the Mother Mary Match-3 (only the rendered glyphs
   differ; the grid logic is identical). The board is a flat array of symbol
   indices; null marks a cell mid-clear. Typed port — identical semantics.
   ========================================================================= */

export const M3_COLS = 6;
export const M3_ROWS = 7;
export const M3_MOVES = 15;
export const M3_SYMBOL_COUNT = 6;

export type Board = (number | null)[];

export const m3at = (r: number, c: number): number => r * M3_COLS + c;
export const m3rand = (): number => Math.floor(Math.random() * M3_SYMBOL_COUNT);

export function makeBoard(): Board {
  const b: Board = new Array(M3_ROWS * M3_COLS);
  for (let r = 0; r < M3_ROWS; r++)
    for (let c = 0; c < M3_COLS; c++) {
      let t: number,
        guard = 0;
      do {
        t = m3rand();
        guard++;
      } while (
        guard < 60 &&
        ((c >= 2 && b[m3at(r, c - 1)] === t && b[m3at(r, c - 2)] === t) || // would make horizontal 3
          (r >= 2 && b[m3at(r - 1, c)] === t && b[m3at(r - 2, c)] === t)) // would make vertical 3
      );
      b[m3at(r, c)] = t;
    }
  return b;
}

export function findMatches(b: Board): Set<number> {
  const cleared = new Set<number>();
  for (let r = 0; r < M3_ROWS; r++) {
    // horizontal runs
    let start = 0;
    for (let c = 1; c <= M3_COLS; c++) {
      const same = c < M3_COLS && b[m3at(r, c)] !== null && b[m3at(r, c)] === b[m3at(r, c - 1)];
      if (!same) {
        if (c - start >= 3) for (let k = start; k < c; k++) cleared.add(m3at(r, k));
        start = c;
      }
    }
  }
  for (let c = 0; c < M3_COLS; c++) {
    // vertical runs
    let start = 0;
    for (let r = 1; r <= M3_ROWS; r++) {
      const same = r < M3_ROWS && b[m3at(r, c)] !== null && b[m3at(r, c)] === b[m3at(r - 1, c)];
      if (!same) {
        if (r - start >= 3) for (let k = start; k < r; k++) cleared.add(m3at(k, c));
        start = r;
      }
    }
  }
  return cleared;
}

export function collapse(b: Board): Board {
  // gravity: survivors fall, tops refill
  for (let c = 0; c < M3_COLS; c++) {
    let write = M3_ROWS - 1;
    for (let r = M3_ROWS - 1; r >= 0; r--) {
      if (b[m3at(r, c)] !== null) {
        b[m3at(write, c)] = b[m3at(r, c)];
        if (write !== r) b[m3at(r, c)] = null;
        write--;
      }
    }
    for (let r = write; r >= 0; r--) b[m3at(r, c)] = m3rand();
  }
  return b;
}

export function swapWouldMatch(b: Board, i: number, j: number): boolean {
  const c = b.slice();
  [c[i], c[j]] = [c[j], c[i]];
  return findMatches(c).size > 0;
}

export function hasPossibleMove(b: Board): boolean {
  for (let r = 0; r < M3_ROWS; r++)
    for (let c = 0; c < M3_COLS; c++) {
      if (c + 1 < M3_COLS && swapWouldMatch(b, m3at(r, c), m3at(r, c + 1))) return true;
      if (r + 1 < M3_ROWS && swapWouldMatch(b, m3at(r, c), m3at(r + 1, c))) return true;
    }
  return false;
}

export function match3Reward(cleared: number): number {
  // cleared-tile count -> cents, $50–$200 band
  if (cleared >= 40) return 20_000; // cap  $200
  if (cleared <= 10) return 5_000; // floor $50
  const dollars = 50 + (cleared - 10) * 5; // linear 10→40 tiles ⇒ $50→$200
  return Math.round(dollars / 5) * 5 * 100;
}
